/**
 * plannedBudgetAdapter.js
 *
 * Phase 5D, Step 2 — Owner for Planned Budget time-series data.
 *
 * Reads project_id + period_month + planned_budget rows from
 * cash_in_planning (added in Step 1, verified working) and returns the
 * canonical TimeSeriesResult shape (Phase 5A) — no financial calculation,
 * pure read + reshape, same posture as every other Adapter in this
 * project.
 *
 * Explicitly separate from planned_cash_in — that column remains
 * Scheduling.jsx/CashFlowExpectation's domain (Cash, not Cost/Budget) and
 * is never read here.
 *
 * Time grain: monthly, regular (period_month), unlike
 * timeSeriesSSOTAdapter's forecastDate points, which are irregular
 * Forecast Revision snapshots. These are two DIFFERENT kinds of "time" —
 * Project Timeline (this Adapter) vs Forecast Observation Dates
 * (timeSeriesSSOTAdapter) — never merged into one series by either
 * Adapter alone. That reconciliation is sCurveSSOTAdapter's job (Step 4,
 * not this file's).
 */

import { supabase } from './supabase'

/**
 * Returns every planned_budget row for a project, as a canonical
 * TimeSeriesResult. grain is always 'project' — planned_budget has no
 * Cost Code breakdown in v1 (per the approved Step 1 decision: "no
 * Direct/Indirect or Cost Code breakdown currently").
 *
 * time on each point is the period_month value, normalized to YYYY-MM-DD
 * (the exact stored format — no reformatting to match forecastDate's
 * shape, since these are a genuinely different kind of time axis, not
 * interchangeable with forecast_history's forecast_date).
 */
export async function getPlannedBudgetSeries(projectId) {
  if (!projectId) return { grain: 'project', series: [] }

  const { data, error } = await supabase
    .from('cash_in_planning')
    .select('period_month, planned_budget')
    .eq('project_id', projectId)
    .order('period_month', { ascending: true })
  if (error) throw error

  const series = (data || [])
    // A row with no planned_budget value at all (never entered, still
    // null/0 from before this column existed) is NOT the same as "user
    // deliberately entered a plan of zero for this month" — but this
    // Adapter has no way to distinguish the two from the stored value
    // alone (there is no separate "was this ever set" flag on
    // cash_in_planning, unlike hasSnapshot's forecast_history existence
    // check). Documented here as a known limitation, not silently
    // resolved: every row present in cash_in_planning for this project
    // is included, with planned_budget defaulting to 0 if null.
    .map(row => ({
      time: row.period_month,
      rows: [{
        key: projectId,
        label: 'Project',
        values: { planned_budget: parseFloat(row.planned_budget) || 0 },
      }],
    }))

  return { grain: 'project', series }
}
